import { useMemo } from "react";
import type { CardPreviewProps } from "@/components/shapes/card-previews";
import { GenericCardPreview } from "@/components/shapes/shared/generic-card-preview";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { framesFileMessages } from "@/i18n/messages/frames-file";
import { cn } from "@/lib/cn";
import {
  fileCardShape,
  hasFile,
  pdfTitle,
  type FileCardShape,
} from "./file-card-shape";
import { FileFace } from "./file-face";
import { PdfCover } from "./pdf-cover";
import { usePdfThumbnail } from "./pdf-thumbnail";
import { useFileNote } from "./use-file-actions";

/**
 * "PDF · 12 pages", the title joining it at five columns, and where a
 * linked PDF is when it is not here (`note`).
 */
function PdfPill({
  title,
  pages,
  note,
}: {
  title: string;
  pages: number | null;
  note: string | null;
}) {
  const t = useT(framesFileMessages);
  return (
    <span
      className="absolute bottom-3 left-3 z-[2] flex max-w-[calc(100%-1.5rem)] items-center gap-1.5 rounded-full bg-[var(--card-chrome)] px-2.5 py-1 text-[11.5px] font-medium text-card-ink shadow-[var(--card-chrome-shadow)]"
      data-testid="file-card-pdf-pill"
    >
      <Icons.FileText className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="hidden min-w-0 truncate [[data-columns='5']_&]:inline">
        {title} ·
      </span>
      <span className="shrink-0">
        {pages ? t("pdfPages", { count: pages }) : "PDF"}
      </span>
      {note && (
        <span
          className="min-w-0 truncate text-card-muted"
          data-testid="file-card-pdf-note"
        >
          · {note}
        </span>
      )}
    </span>
  );
}

/**
 * A PDF (mockup `.sheet`, Proposed): its first page drawn by pdf.js, the
 * same pixels the preview shows, else a calm paper sheet with the title
 * while the page is drawn. Never a coloured cover.
 */
function PdfCard({
  frameId,
  source,
  shape,
  title,
  note,
  compact,
}: {
  frameId: string;
  source: Record<string, unknown>;
  shape: Extract<FileCardShape, { kind: "pdf" }>;
  title: string;
  note: string | null;
  compact: boolean;
}) {
  usePdfThumbnail(frameId, source, !shape.url);
  return (
    <div
      className={cn("relative overflow-hidden", compact && "h-full")}
      style={compact ? undefined : { aspectRatio: shape.ratio }}
      data-testid="file-card-pdf"
    >
      <PdfCover thumbnailUrl={shape.url} title={title} />
      <PdfPill title={title} pages={shape.pages} note={note} />
    </div>
  );
}

/**
 * A file: a PDF shows its first page; any other file is a calm card with
 * its type glyph and its name, no tint.
 */
export function FileCardPreview(props: CardPreviewProps) {
  const { frame, source, density } = props;
  const shape = useMemo(() => fileCardShape(frame), [frame]);
  const note = useFileNote(source);
  if (shape.kind === "pdf") {
    return (
      <PdfCard
        frameId={frame.frameId}
        source={source}
        shape={shape}
        title={pdfTitle(frame)}
        note={note}
        compact={density === "compact"}
      />
    );
  }
  if (!hasFile(source)) return <GenericCardPreview {...props} />;
  return (
    <FileFace
      source={source}
      density={density}
      note={note}
      testIdPrefix="file-card"
    />
  );
}
